<script setup>
import { ref, reactive } from 'vue';
import { X, Bug, Lightbulb, Loader2 } from 'lucide-vue-next';
import ReportAnnotator from './ReportAnnotator.vue';
import { useReportsStore } from '../../../stores/reports';
import { compressImage } from '../../../lib/imageUtils';

const props = defineProps({
  screenshot: { type: String, default: '' }, // dataURL capture
  context: { type: Object, default: () => ({}) },
  consoleErrors: { type: Array, default: () => [] },
});
const emit = defineEmits(['close', 'sent']);

const reports = useReportsStore();
const annotatorRef = ref(null);
const submitting = ref(false);
const errorMsg = ref('');

const form = reactive({
  type: 'bug',
  priorite: 'moyenne',
  titre: '',
  commentaire: '',
});

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(blob);
  });
}

async function submit() {
  errorMsg.value = '';
  if (!form.titre.trim()) {
    errorMsg.value = 'Donne un titre au rapport.';
    return;
  }
  submitting.value = true;
  try {
    const screenshotBase64 = annotatorRef.value
      ? annotatorRef.value.exportDataUrl()
      : props.screenshot;

    let thumbnailBase64 = null;
    if (screenshotBase64) {
      const thumbBlob = await compressImage(screenshotBase64, { maxWidth: 220, quality: 0.6 });
      thumbnailBase64 = await blobToDataUrl(thumbBlob);
    }

    await reports.submitReport({
      type: form.type,
      priorite: form.priorite,
      titre: form.titre.trim(),
      commentaire: form.commentaire.trim(),
      screenshotBase64,
      thumbnailBase64,
      context: props.context,
      consoleErrors: props.consoleErrors,
    });
    emit('sent');
  } catch (e) {
    errorMsg.value = (e && e.message) || "Échec de l'envoi. Réessaie.";
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div data-report-ui class="fixed inset-0 z-[9998] bg-black/60 flex items-center justify-center p-3 sm:p-4">
    <div class="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col md:flex-row">
      <!-- Capture annotable -->
      <div class="md:w-3/5 p-4 border-b md:border-b-0 md:border-r border-gray-100 overflow-auto">
        <h3 class="font-bold text-sm text-gray-900 mb-2">Capture</h3>
        <ReportAnnotator ref="annotatorRef" :image="screenshot" />
      </div>
      <!-- Formulaire -->
      <div class="md:w-2/5 p-4 flex flex-col gap-3 overflow-auto">
        <div class="flex items-center justify-between">
          <h3 class="font-bold text-base text-gray-900">Nouveau rapport</h3>
          <button type="button" class="text-gray-400 hover:text-gray-900" @click="emit('close')"><X class="w-5 h-5" /></button>
        </div>

        <div class="grid grid-cols-2 gap-2">
          <button type="button" :class="['flex items-center justify-center gap-2 p-2 rounded-lg border text-sm', form.type === 'bug' ? 'border-[#e94591] bg-[#e94591]/10 text-[#e94591]' : 'border-gray-200 text-gray-600']" @click="form.type = 'bug'"><Bug class="w-4 h-4" /> Bug</button>
          <button type="button" :class="['flex items-center justify-center gap-2 p-2 rounded-lg border text-sm', form.type === 'suggestion' ? 'border-[#0077B6] bg-[#0077B6]/10 text-[#0077B6]' : 'border-gray-200 text-gray-600']" @click="form.type = 'suggestion'"><Lightbulb class="w-4 h-4" /> Suggestion</button>
        </div>

        <div>
          <label class="text-xs font-semibold text-gray-500">Priorité</label>
          <select v-model="form.priorite" class="w-full border border-gray-200 rounded-lg p-2 text-sm mt-1">
            <option value="basse">Basse</option>
            <option value="moyenne">Moyenne</option>
            <option value="haute">Haute</option>
            <option value="critique">Critique</option>
          </select>
        </div>

        <div>
          <label class="text-xs font-semibold text-gray-500">Titre</label>
          <input v-model="form.titre" type="text" maxlength="120" placeholder="Résumé court du problème" class="w-full border border-gray-200 rounded-lg p-2 text-sm mt-1" />
        </div>

        <div class="flex-1 flex flex-col">
          <label class="text-xs font-semibold text-gray-500">Commentaire</label>
          <textarea v-model="form.commentaire" rows="5" maxlength="4000" placeholder="Décris ce qui se passe, étapes pour reproduire..." class="w-full flex-1 border border-gray-200 rounded-lg p-2 text-sm mt-1 resize-none"></textarea>
        </div>

        <p v-if="errorMsg" class="text-xs text-red-600">{{ errorMsg }}</p>

        <button type="button" :disabled="submitting" class="bg-[#e94591] text-white font-bold rounded-xl p-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60" @click="submit">
          <Loader2 v-if="submitting" class="w-4 h-4 animate-spin" />
          {{ submitting ? 'Envoi...' : 'Envoyer le rapport' }}
        </button>
      </div>
    </div>
  </div>
</template>
